import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Autorenew,
  CheckCircle,
  DeleteOutline,
  ErrorOutline,
  Memory,
  MonitorHeart,
  Storage,
  WarningAmber,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { adminAPI } from '../../services/api';
import { tokens } from '../../theme';
import { adminTokens, formatRelativeTimeLong, panelSx } from './adminTokens';

const { border: BORDER, editorialSerif } = adminTokens;

const STATUS_COLORS = {
  operational: tokens.success,
  degraded: tokens.clay,
  critical: tokens.danger,
  healthy: tokens.success,
  configured: tokens.success,
  'dev-mode': tokens.clay,
  missing: tokens.danger,
};

const LOG_LEVEL_COLORS = {
  error: tokens.danger,
  warn: tokens.clay,
  info: tokens.accentStrong,
  debug: tokens.textMuted,
  http: '#8ec8d7',
};

function formatUptime(seconds) {
  if (!seconds && seconds !== 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m ${seconds % 60}s`;
}

function StatusIcon({ status }) {
  if (status === 'operational' || status === 'healthy' || status === 'configured') {
    return <CheckCircle sx={{ fontSize: 18, color: tokens.success }} />;
  }
  if (status === 'degraded' || status === 'dev-mode') {
    return <WarningAmber sx={{ fontSize: 18, color: tokens.clay }} />;
  }
  return <ErrorOutline sx={{ fontSize: 18, color: tokens.danger }} />;
}

function MetricTile({ label, value, sub }) {
  return (
    <Box sx={panelSx({ p: 1.5, height: '100%' })}>
      <Typography variant="caption" sx={{ color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ color: tokens.cream, mt: 0.4, fontFamily: editorialSerif }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mt: 0.3 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function ServiceCard({ service }) {
  const score = service.status;
  const color = score >= 90 ? tokens.success : score >= 60 ? tokens.clay : tokens.danger;
  return (
    <Box sx={panelSx({ p: 1.75, height: '100%' })}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" sx={{ color: tokens.cream, fontWeight: 500 }}>{service.name}</Typography>
          {service.detail && (
            <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mt: 0.4 }}>
              {service.detail}
            </Typography>
          )}
        </Box>
        <Chip
          label={`${score}%`}
          size="small"
          sx={{
            bgcolor: `${color}22`,
            color,
            border: `1px solid ${color}44`,
            borderRadius: 0,
            fontWeight: 600,
          }}
        />
      </Stack>
      <Box sx={{ mt: 1.25, height: 4, bgcolor: 'rgba(255,255,255,0.06)' }}>
        <Box sx={{ width: `${score}%`, height: '100%', bgcolor: color, transition: 'width 0.4s ease' }} />
      </Box>
    </Box>
  );
}

function LogRow({ entry }) {
  const levelColor = LOG_LEVEL_COLORS[entry.level] || tokens.textMuted;
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderBottom: `1px solid ${BORDER}`,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '0.72rem',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
        <Typography component="span" sx={{ color: tokens.textMuted, minWidth: 72 }}>
          {new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </Typography>
        <Chip
          label={entry.level}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.62rem',
            borderRadius: 0,
            bgcolor: `${levelColor}18`,
            color: levelColor,
            border: `1px solid ${levelColor}33`,
          }}
        />
        <Typography component="span" sx={{ color: tokens.textMuted, fontSize: '0.68rem' }}>
          [{entry.source}]
        </Typography>
        <Typography component="span" sx={{ color: tokens.cream, flex: 1, wordBreak: 'break-word' }}>
          {entry.message}
        </Typography>
      </Stack>
    </Box>
  );
}

export default function AdminSystemHealthView() {
  const [tab, setTab] = useState(0);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState({ items: [], total: 0, levelCounts: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [logLevel, setLogLevel] = useState('all');
  const [logSearch, setLogSearch] = useState('');
  const [error, setError] = useState('');

  const loadHealth = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError('');
    try {
      const [healthRes, logsRes] = await Promise.all([
        adminAPI.getDetailedHealth(),
        adminAPI.getSystemLogs({ level: logLevel, search: logSearch, limit: 150 }),
      ]);
      setHealth(healthRes.data?.data || null);
      setLogs(logsRes.data?.data || { items: [], total: 0, levelCounts: {} });
    } catch (err) {
      setError(err.message || 'Failed to load system health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [logLevel, logSearch]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => loadHealth(true), 10000);
    return () => clearInterval(id);
  }, [autoRefresh, loadHealth]);

  const statusChartData = useMemo(() => {
    const counts = health?.requests?.statusCounts || {};
    return Object.entries(counts)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([code, count]) => ({ code, count }));
  }, [health]);

  const topPaths = health?.requests?.topPaths || [];

  if (loading && !health) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress sx={{ color: tokens.accentStrong }} />
          <Typography variant="body2" sx={{ color: tokens.textSoft }}>Loading system health...</Typography>
        </Stack>
      </Box>
    );
  }

  const statusColor = STATUS_COLORS[health?.status] || tokens.textMuted;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={panelSx({ p: 2 })}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center', border: `1px solid ${BORDER}`, color: statusColor }}>
              <MonitorHeart />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>
                System Health
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
                <StatusIcon status={health?.status} />
                <Typography variant="body2" sx={{ color: statusColor, textTransform: 'capitalize' }}>
                  {health?.status || 'unknown'}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                  · v{health?.version} · {health?.environment}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Switch
                size="small"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>Auto-refresh (10s)</Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={14} /> : <Autorenew />}
              onClick={() => loadHealth(true)}
              disabled={refreshing}
            >
              Refresh
            </Button>
            <Typography variant="caption" sx={{ color: tokens.textMuted }}>
              Updated {formatRelativeTimeLong(health?.timestamp)}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Box sx={panelSx({ p: 1.5, borderColor: `${tokens.danger}55` })}>
          <Typography variant="body2" sx={{ color: tokens.danger }}>{error}</Typography>
        </Box>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={6} sm={3}>
          <MetricTile label="Uptime" value={formatUptime(health?.uptimeSeconds)} sub={`PID ${health?.process?.pid || '—'}`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricTile label="Requests" value={health?.requests?.totalRequests?.toLocaleString() || '0'} sub={`${health?.requests?.errorRate || 0}% error rate`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricTile label="Avg latency" value={`${health?.requests?.avgLatencyMs || 0}ms`} sub={`p95 ${health?.requests?.p95LatencyMs || 0}ms`} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricTile label="Heap used" value={health?.process?.memory?.heapUsedFormatted || '—'} sub={`of ${health?.process?.memory?.heapTotalFormatted || '—'}`} />
        </Grid>
      </Grid>

      <Grid container spacing={1.5}>
        {(health?.services || []).map((service) => (
          <Grid item xs={12} sm={6} md={4} lg key={service.name}>
            <ServiceCard service={service} />
          </Grid>
        ))}
      </Grid>

      <Box sx={panelSx({ p: 0 })}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            borderBottom: `1px solid ${BORDER}`,
            px: 1.5,
            minHeight: 42,
            '& .MuiTab-root': { color: tokens.textMuted, minHeight: 42, fontSize: '0.82rem' },
            '& .Mui-selected': { color: tokens.cream },
            '& .MuiTabs-indicator': { bgcolor: tokens.accentStrong },
          }}
        >
          <Tab label="Infrastructure" />
          <Tab label="Request metrics" />
          <Tab label={`Logs (${logs.total})`} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tab === 0 && (
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Box sx={panelSx({ p: 1.75 })}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                    <Storage sx={{ color: tokens.accentStrong, fontSize: 18 }} />
                    <Typography variant="subtitle2" sx={{ color: tokens.cream }}>Database</Typography>
                    <Chip
                      label={health?.database?.status || 'unknown'}
                      size="small"
                      sx={{ ml: 'auto', borderRadius: 0, fontSize: '0.68rem', bgcolor: `${STATUS_COLORS[health?.database?.status] || tokens.textMuted}22`, color: STATUS_COLORS[health?.database?.status] }}
                    />
                  </Stack>
                  <Stack spacing={0.6}>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>State: {health?.database?.state}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>Host: {health?.database?.host || '—'} / {health?.database?.name || '—'}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>Ping: {health?.database?.pingMs != null ? `${health.database.pingMs}ms` : '—'}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>Collections: {health?.database?.collectionCount || 0}</Typography>
                    {health?.database?.stats && (
                      <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        Data: {health.database.stats.dataSizeFormatted} · Objects: {health.database.stats.objects?.toLocaleString()}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={panelSx({ p: 1.75 })}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                    <Memory sx={{ color: tokens.accentStrong, fontSize: 18 }} />
                    <Typography variant="subtitle2" sx={{ color: tokens.cream }}>Process & host</Typography>
                  </Stack>
                  <Stack spacing={0.6}>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>Node {health?.process?.nodeVersion} · {health?.process?.platform}/{health?.process?.arch}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>RSS: {health?.process?.memory?.rssFormatted}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>System memory free: {health?.process?.freeMemoryFormatted} / {health?.process?.totalMemoryFormatted}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>Load avg: {(health?.process?.loadAverage || []).map((v) => v.toFixed(2)).join(', ')}</Typography>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={panelSx({ p: 1.75 })}>
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1 }}>Storage directories</Typography>
                  <Stack spacing={0.75}>
                    {(health?.storage?.directories || []).map((dir) => (
                      <Box key={dir.name} sx={{ p: 1, border: `1px solid ${BORDER}` }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ color: tokens.cream, fontSize: '0.82rem' }}>{dir.name}</Typography>
                          <Chip label={dir.status} size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.65rem' }} />
                        </Stack>
                        <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                          {dir.fileCount} files · {dir.totalBytesFormatted} · {dir.writable ? 'writable' : 'read-only'}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={panelSx({ p: 1.75 })}>
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1 }}>Configuration</Typography>
                  <Stack spacing={0.75}>
                    <Box sx={{ p: 1, border: `1px solid ${BORDER}` }}>
                      <Typography variant="body2" sx={{ color: tokens.cream, fontSize: '0.82rem' }}>Email</Typography>
                      <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        {health?.email?.mode} · {health?.email?.status}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1, border: `1px solid ${BORDER}` }}>
                      <Typography variant="body2" sx={{ color: tokens.cream, fontSize: '0.82rem' }}>Auth / JWT</Typography>
                      <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        {health?.auth?.jwtConfigured ? 'Configured' : 'Missing JWT secret'}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1, border: `1px solid ${BORDER}` }}>
                      <Typography variant="body2" sx={{ color: tokens.cream, fontSize: '0.82rem' }}>Security</Typography>
                      <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                        Helmet · Rate limit · CORS: {(health?.security?.corsOrigins || []).join(', ')}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={7}>
                <Box sx={panelSx({ p: 1.75 })}>
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.5 }}>Response status codes</Typography>
                  <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={statusChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="code" tick={{ fill: tokens.textMuted, fontSize: 11 }} />
                        <YAxis tick={{ fill: tokens.textMuted, fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#111915', border: `1px solid ${BORDER}`, color: tokens.cream }}
                        />
                        <Bar dataKey="count" fill={tokens.accentStrong} radius={0} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={panelSx({ p: 1.75, height: '100%' })}>
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1 }}>Top endpoints</Typography>
                  <Stack spacing={0.5}>
                    {topPaths.length === 0 && (
                      <Typography variant="caption" sx={{ color: tokens.textMuted }}>No requests recorded yet</Typography>
                    )}
                    {topPaths.map((item) => (
                      <Stack key={item.path} direction="row" justifyContent="space-between" sx={{ py: 0.35, borderBottom: `1px solid ${BORDER}` }}>
                        <Typography variant="caption" sx={{ color: tokens.cream, fontFamily: 'monospace', fontSize: '0.72rem', maxWidth: '75%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.path}
                        </Typography>
                        <Typography variant="caption" sx={{ color: tokens.textMuted }}>{item.count}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={panelSx({ p: 1.75 })}>
                  <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1 }}>Recent requests</Typography>
                  <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                    {(health?.requests?.recentRequests || []).map((req, i) => (
                      <Stack
                        key={`${req.timestamp}-${i}`}
                        direction="row"
                        spacing={1}
                        sx={{ py: 0.5, borderBottom: `1px solid ${BORDER}`, fontFamily: 'monospace', fontSize: '0.72rem' }}
                      >
                        <Typography component="span" sx={{ color: tokens.textMuted, minWidth: 52 }}>{req.method}</Typography>
                        <Typography component="span" sx={{ color: req.statusCode >= 500 ? tokens.danger : req.statusCode >= 400 ? tokens.clay : tokens.cream, minWidth: 36 }}>
                          {req.statusCode}
                        </Typography>
                        <Typography component="span" sx={{ color: tokens.cream, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.path}</Typography>
                        <Typography component="span" sx={{ color: tokens.textMuted }}>{req.durationMs}ms</Typography>
                      </Stack>
                    ))}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}

          {tab === 2 && (
            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 1.5 }} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: tokens.textMuted }}>Level</InputLabel>
                  <Select
                    value={logLevel}
                    label="Level"
                    onChange={(e) => setLogLevel(e.target.value)}
                    sx={{ color: tokens.cream }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="warn">Warn</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="http">HTTP</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="Search logs..."
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  sx={{ flex: 1, maxWidth: 360 }}
                  InputProps={{ sx: { color: tokens.cream, fontSize: '0.85rem' } }}
                />
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {Object.entries(logs.levelCounts || {}).map(([level, count]) => (
                    <Chip
                      key={level}
                      label={`${level}: ${count}`}
                      size="small"
                      onClick={() => setLogLevel(level)}
                      sx={{ borderRadius: 0, fontSize: '0.68rem', bgcolor: `${LOG_LEVEL_COLORS[level] || tokens.textMuted}18`, color: LOG_LEVEL_COLORS[level] }}
                    />
                  ))}
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={async () => {
                    await adminAPI.clearSystemLogs();
                    loadHealth(true);
                  }}
                >
                  Clear
                </Button>
              </Stack>
              <Box sx={{ ...panelSx({ p: 0 }), maxHeight: 420, overflow: 'auto' }}>
                {logs.items.length === 0 ? (
                  <Typography variant="body2" sx={{ color: tokens.textMuted, p: 2 }}>No log entries match your filters.</Typography>
                ) : (
                  logs.items.map((entry) => <LogRow key={entry.id} entry={entry} />)
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
